import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  useTheme,
  useMediaQuery,
  Fade
} from '@mui/material';
import {
  CheckCircle,
  ArrowForward,
  Star,
  AccessTime,
  CalendarToday,
  DateRange,
  ArrowBackIosNew as ArrowBackIos,
  ArrowForwardIos
} from '@mui/icons-material';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useLanguage } from '../../../localization';
import tokens from '../../../theme/tokens';

const Pricing = ({ onGetStarted }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useLanguage();

  // Core features to highlight (reduced list)
  const keyFeatures = [
    t('pricing.features.unlimitedResumes'),
    t('pricing.features.allTemplates'),
    t('pricing.features.privacyProtection'),
    t('pricing.features.atsOptimized'),
    t('pricing.features.noSignup')
  ];

  // Pricing options
  const pricingOptions = [
    {
      id: 'pdf_one_time',
      icon: <CheckCircle sx={{ fontSize: 28 }} />,
      name: t('payment.products.pdf_one_time.name'),
      price: '€0.99',
      description: t('payment.products.pdf_one_time.description'),
      popular: false,
      features: [
        t('pricing.passFeatures.singleDownload'),
        'All professional templates',
        'Perfect for single application'
      ]
    },
    {
      id: 'pdf_24h',
      icon: <AccessTime sx={{ fontSize: 28 }} />,
      name: t('payment.products.pdf_24h.name'),
      price: '€1.99',
      description: t('payment.products.pdf_24h.description'),
      popular: false,
      features: [
        'Unlimited downloads (24h)',
        'All templates & customization',
        'Great for quick revisions'
      ]
    },
    {
      id: 'pdf_week',
      icon: <CalendarToday sx={{ fontSize: 28 }} />,
      name: t('payment.products.pdf_week.name'),
      price: '€4.99',
      description: t('payment.products.pdf_week.description'),
      popular: true,
      features: [
        'Unlimited downloads (7 days)',
        'All templates & customization',
        'Perfect for job hunting'
      ]
    },
    {
      id: 'pdf_month',
      icon: <DateRange sx={{ fontSize: 28 }} />,
      name: t('payment.products.pdf_month.name'),
      price: '€7.99',
      description: t('payment.products.pdf_month.description'),
      popular: false,
      features: [
        'Unlimited downloads (30 days)',
        'All templates & customization',
        'Best value for extended use'
      ]
    }
  ];

  const CustomArrow = (props) => {
    const { className, style, onClick, direction, icon } = props;
    // Hide arrows on small mobile, as dots are sufficient and space is limited
    if (isSmallMobile) {
      return null;
    }
    return (
      <Box
        className={className}
        sx={{
          ...style,
          display: 'block',
          color: tokens.colors.neutral[500],
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          borderRadius: '50%',
          width: 40, 
          height: 40,
          zIndex: 2,
          '&:hover': {
            color: tokens.colors.primary.main,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
          },
          '&::before': {
            display: 'none' // Remove default slick arrow styling
          },
          // Custom positioning
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          ...(direction === 'prev' ? { left: '-20px' } : { right: '-20px' }),
          // Hide on medium and down if not explicitly styled for them
          [theme.breakpoints.down('md')]: {
             ...(direction === 'prev' ? { left: '-5px' } : { right: '-5px' }),
          },
           [theme.breakpoints.down('sm')]: {
            display: 'none', // Explicitly hide on small screens (sm and xs)
          }
        }}
        onClick={onClick}
      >
        {icon}
      </Box>
    );
  };

  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    nextArrow: <CustomArrow direction="next" icon={<ArrowForwardIos sx={{ fontSize: '1.2rem', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />} />,
    prevArrow: <CustomArrow direction="prev" icon={<ArrowBackIos sx={{ fontSize: '1.2rem', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />} />,
    responsive: [
      {
        breakpoint: 1024, // md
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          infinite: true,
          dots: true,
          arrows: !isMobile // Show arrows only if not mobile (md and up)
        }
      },
      {
        breakpoint: 600, // sm
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false, // Ensure arrows are off for small mobile
          dots: true // Keep dots on mobile for navigation
        }
      }
    ]
  };

  const PricingCard = ({ option, index }) => (
    <Box sx={{ p: 1, height: '100%' }}>
      <Fade in timeout={800 + index * 200}>
        <Card
          sx={{
            height: '100%',
            backgroundColor: 'white',
            border: option.popular 
              ? `2px solid ${tokens.colors.primary.main}` 
              : `1px solid ${tokens.colors.borders.light}`,
            borderRadius: tokens.radius.xl,
            boxShadow: option.popular ? tokens.shadows.lg : tokens.shadows.sm,
            position: 'relative',
            overflow: 'visible',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: tokens.shadows.lg
            }
          }}
        >
          {/* Popular badge */}
          {option.popular && (
            <Chip
              icon={<Star sx={{ fontSize: 16 }} />}
              label={t('pricing.mostPopular')}
              size="small"
              sx={{
                position: 'absolute',
                top: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: tokens.colors.primary.main,
                color: 'white',
                fontWeight: tokens.typography.fontWeights.semibold,
                '& .MuiChip-icon': {
                  color: 'white'
                }
              }}
            />
          )}

          <CardContent sx={{ 
            p: { xs: 3, md: 4 }, 
            textAlign: 'center',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Icon */}
            <Box
              sx={{
                color: tokens.colors.primary.main,
                mb: 2
              }}
            >
              {option.icon}
            </Box>

            {/* Plan name */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.bold,
                color: tokens.colors.text.primary,
                mb: 1
              }}
            >
              {option.name}
            </Typography>

            {/* Price */}
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: tokens.typography.fontWeights.bold,
                color: tokens.colors.primary.main,
                lineHeight: 1,
                mb: 2
              }}
            >
              {option.price}
            </Typography>

            {/* Description */}
            <Typography
              variant="body2"
              sx={{
                color: tokens.colors.text.secondary,
                mb: 3,
                minHeight: '2.5rem'
              }}
            >
              {option.description}
            </Typography>

            {/* Features */}
            <Box sx={{ flex: 1, mb: 3 }}>
              <Stack spacing={1.5}>
                {option.features.slice(0, 3).map((feature, featureIndex) => (
                  <Box key={featureIndex} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircle 
                      sx={{ 
                        color: tokens.colors.success, 
                        fontSize: 16,
                        mr: 1,
                        flexShrink: 0
                      }} 
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: tokens.colors.text.primary,
                        fontSize: '0.85rem',
                        textAlign: 'left',
                        lineHeight: 1.3
                      }}
                    >
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* CTA Button */}
            <Button
              variant={option.popular ? "contained" : "outlined"}
              size="large"
              onClick={onGetStarted}
              fullWidth
              sx={{
                backgroundColor: option.popular ? tokens.colors.primary.main : 'transparent',
                color: option.popular ? 'white' : tokens.colors.primary.main,
                borderColor: tokens.colors.primary.main,
                fontWeight: tokens.typography.fontWeights.semibold,
                textTransform: 'none',
                py: 1.5,
                fontSize: '1rem',
                borderRadius: tokens.radius.base,
                '&:hover': {
                  backgroundColor: option.popular 
                    ? tokens.colors.primary.dark 
                    : `${tokens.colors.primary.main}10`,
                  borderColor: tokens.colors.primary.dark,
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {t('pricing.cta')}
            </Button>
          </CardContent>
        </Card>
      </Fade>
    </Box>
  );

  return (
    <Box
      id="pricing"
      sx={{
        py: { xs: 6, md: 8 },
        backgroundColor: 'white',
        position: 'relative',
        // Add some padding on the sides for the arrows on larger screens
        px: { xs: 0, md: '40px' }
      }}
    >
      <Container maxWidth="lg">
        {/* Compact header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.8rem', md: '2.2rem', lg: '2.5rem' },
              fontWeight: tokens.typography.fontWeights.bold,
              color: tokens.colors.text.primary,
              mb: 1
            }}
          >
            {t('pricing.title')}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: { xs: '0.9rem', md: '1rem' },
              color: tokens.colors.text.secondary,
              maxWidth: '500px',
              mx: 'auto'
            }}
          >
            {t('pricing.subtitle')}
          </Typography>
        </Box>

        {/* Pricing Cards - Desktop Grid / Mobile Carousel */}
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
          {/* Desktop Grid */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Grid container spacing={3} justifyContent="center">
              {pricingOptions.map((option, index) => (
                <Grid item xs={12} sm={6} md={3} key={option.id}>
                  <PricingCard option={option} index={index} />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Mobile Carousel */}
          <Box sx={{ 
            display: { xs: 'block', md: 'none' },
            mb: { xs: 6, sm: 4 }, // Add bottom margin to prevent dots overlap
            '.slick-slide > div': { margin: '0 10px' }, 
            '.slick-list': { margin: '0 -10px'},
            '.slick-slide': { height: 'auto' },
            '.slick-track': { display: 'flex', alignItems: 'stretch' },
            '.slick-dots': {
              bottom: '-40px',
              '& li button:before': {
                color: tokens.colors.primary.main,
                fontSize: '12px'
              },
              '& li.slick-active button:before': {
                color: tokens.colors.primary.main
              }
            }
          }}>
            <Slider {...sliderSettings}>
              {pricingOptions.map((option, index) => (
                <PricingCard key={option.id} option={option} index={index} />
              ))}
            </Slider>
          </Box>
        </Box>

        {/* Why Pay Per Use Section - Condensed */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: tokens.typography.fontWeights.semibold,
              color: tokens.colors.text.primary,
              mb: 2,
              fontSize: { xs: '1.2rem', md: '1.4rem' }
            }}
          >
            {t('pricing.whyTimeLimited.title')}
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              color: tokens.colors.text.secondary,
              lineHeight: 1.6,
              maxWidth: '700px',
              mx: 'auto',
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}
          >
            {t('pricing.whyTimeLimited.description')}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Pricing; 